import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["option"],
  _excluded2 = ["option"],
  _excluded3 = ["option", "children"];
import { SearchAutocompleteResultItem } from './SearchAutocompleteResultItem';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from 'foundations-components/components/Text';
import { List } from 'foundations-components/transitional/components/List';
import FormattedList from 'I18n/components/FormattedList';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { View } from 'foundations-components/components/View';
import { useLocation } from 'quartz-routing/react-router';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { useTheme } from 'foundations-theming/utils';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { getResultsPagePath } from './hooks/useNavigateToResultsPage';
import { isOnResultsPage } from './utils/isOnResultsPage';
import { ACContentCardSmall } from 'academy-common-lib/components/card/ACContentCardSmall';
import { Small } from 'foundations-components/transitional/components/Small';
import SearchAutocompleteResultsHeader from './SearchAutocompleteResultsHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OptionsTitle = ({
  children,
  id
}) => {
  return /*#__PURE__*/_jsx(Text, {
    id: id,
    paddingInline: "500",
    paddingBlockEnd: "200",
    paddingBlockStart: "500",
    textTransform: "uppercase",
    typography: "heading-50",
    children: children
  });
};
export const getOptionsTitleId = index => {
  return `autocomplete-options-title-${index}`;
};
const highlightText = (resultText, searchValue) => {
  const index = resultText.toLowerCase().indexOf(searchValue.toLowerCase());
  if (index === -1) {
    return resultText;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [resultText.slice(0, index), /*#__PURE__*/_jsx("strong", {
      children: resultText.slice(index, index + searchValue.length)
    }), resultText.slice(index + searchValue.length)]
  });
};
const PopularTermOptionButton = _ref => {
  let {
      option
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!(option !== null && option !== void 0 && option.text)) {
    return null;
  }
  const {
    searchValue
  } = option;
  return /*#__PURE__*/_jsx(Button, Object.assign({}, rest, {
    block: true,
    paddingBlock: "200",
    paddingInline: "500",
    use: "unstyled",
    children: /*#__PURE__*/_jsx(View, {
      inlineSize: "100%",
      textAlign: "start",
      children: highlightText(option.text, searchValue)
    })
  }));
};
const PopularTermOptionLink = _ref2 => {
  let {
      option
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const location = useLocation();
  if (!(option !== null && option !== void 0 && option.text)) {
    return null;
  }
  const replace = isOnResultsPage(location.pathname);
  return /*#__PURE__*/_jsx(RouterLink, Object.assign({}, rest, {
    replace: replace,
    to: getResultsPagePath(replace ? location.search : undefined, {
      search: option.text
    }),
    use: "unstyled",
    children: /*#__PURE__*/_jsx(PopularTermOptionButton, {
      option: option
    })
  }));
};
export const makeInlineContentCardUrl = (option, location) => {
  const {
    contentType: resourceType,
    id: resourceId,
    language
  } = option;
  switch (resourceType) {
    case 'SHORT_FORM_CONTENT':
      {
        return `/shortvideo/${resourceId}`;
      }
    case 'LEARNING_PATH':
      {
        return `/saved-items/playlists/${resourceId}`;
      }
    default:
      {
        const searchParams = new URLSearchParams(location.search);
        const modalParams = getOverviewModalParams({
          id: resourceId,
          type: readUnhandledEnumCase(resourceType),
          language: readUnhandledEnumCase(language)
        });
        Object.entries(modalParams).forEach(([key, value]) => {
          searchParams.set(key, value);
        });
        return `${location.pathname}?${searchParams}`;
      }
  }
};
const QuickLinkOptionCard = _ref3 => {
  let {
      option
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const location = useLocation();
  const to = makeInlineContentCardUrl(option, location);
  const {
    duration: resourceDuration,
    name: resourceName,
    resourceThumbnail,
    contentType: resourceType,
    certificate,
    numberOfItems,
    numberOfLessons
  } = option;
  const makeResourceMetadata = () => {
    const metadata = [];
    if (numberOfItems) {
      metadata.push( /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "playlist-total-items",
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.playlist.items",
        options: {
          count: numberOfItems
        }
      }, "playlist-total-items"));
    }
    if (numberOfLessons) {
      metadata.push( /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "track-total-lessons",
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.track.lessons",
        options: {
          count: numberOfLessons
        }
      }, "track-total-lessons"));
    }
    if (resourceDuration) {
      metadata.push( /*#__PURE__*/_jsx(FormattedDuration, {
        format: "medium",
        from: +new Date() - resourceDuration * 1000
      }, "lesson-duration"));
    }
    return metadata;
  };
  const getResourceTypeMessage = () => {
    if (certificate) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.certificate"
      });
    }
    switch (resourceType) {
      case 'TRACK':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.track"
        });
      case 'LESSON':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.lesson"
        });
      case 'SHORT_FORM_CONTENT':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.shortFormContent"
        });
      case 'LEARNING_PATH':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.learningPath"
        });
      default:
        return null;
    }
  };
  const theme = useTheme();
  return (
    /*#__PURE__*/
    // Raw div is needed to allow the rest props to be applied correctly - View doesn't pass down all the rest props
    _jsx("div", Object.assign({}, rest, {
      style: {
        paddingBlock: theme.space['200'],
        paddingInline: theme.space['500']
      },
      children: /*#__PURE__*/_jsx(ACContentCardSmall, {
        bordered: false,
        contentHref: to,
        disableTooltip: true,
        linkMode: "react-router",
        length: (resourceType === 'LEARNING_PATH' ? numberOfItems : resourceType === 'TRACK' ? numberOfLessons : resourceDuration) || 0,
        footer: /*#__PURE__*/_jsx(Small, {
          color: "text-core-subtle",
          children: /*#__PURE__*/_jsxs(List, {
            inline: true,
            use: "inline-divided",
            wrapChildren: true,
            children: [getResourceTypeMessage(), /*#__PURE__*/_jsx(FormattedList, {
              exclusive: true,
              children: makeResourceMetadata()
            })]
          })
        }),
        name: resourceName,
        imageSrc: resourceThumbnail,
        size: 70
      })
    }))
  );
};
const makeResults = ({
  disableResultsNavigation = false,
  searchValue,
  suggestions
}) => {
  let totalOptions = 0;
  return Object.keys(suggestions).map(suggestionType => {
    switch (suggestionType) {
      case 'popularTerms':
        {
          return {
            itemComponent: disableResultsNavigation ? PopularTermOptionButton : PopularTermOptionLink,
            type: 'popularTerms',
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.title.popularTerms"
            }),
            options: suggestions.popularTerms.map(option => {
              return {
                text: option.term,
                value: option.term,
                searchValue
              };
            })
          };
        }
      case 'quickLinks':
        {
          return {
            itemComponent: QuickLinkOptionCard,
            type: 'quickLinks',
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.title.quickLinks"
            }),
            options: suggestions.quickLinks.map(option => {
              return Object.assign({
                text: option.name,
                value: option.name
              }, option);
            })
          };
        }
      default:
        {
          return null;
        }
    }
  }).filter(item => item && item.options.length > 0).map(item => {
    item = item;
    const startIndex = totalOptions;
    totalOptions += item.options.length;
    return Object.assign({}, item, {
      title: /*#__PURE__*/_jsx(OptionsTitle, {
        id: getOptionsTitleId(startIndex),
        children: item.title
      }),
      startIndex
    });
  });
};
export const SearchAutocompleteResults = ({
  disableResultsNavigation = false,
  highlightedOptionIndex,
  inputRef,
  onResultItemMouseEnter,
  onResultItemClick,
  onHeaderClick,
  searchValue,
  suggestions
}) => {
  const [maxBlockSize, setMaxBlockSize] = useState('none');
  const containerRef = useRef(null);
  useEffect(() => {
    const input = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
    const container = containerRef.current;
    if (!container || !input) {
      return;
    }
    const availableBottomSpace = window.innerHeight - input.getBoundingClientRect().bottom;
    if (container.scrollHeight > availableBottomSpace) {
      // Use 0.98 multiplier to leave a small gap between the dropdown and viewport bottom
      // rather than having it touch the edge
      const newMaxBlockSize = Math.max(0.98 * availableBottomSpace, 300);
      setMaxBlockSize(`${newMaxBlockSize}px`);
    } else {
      setMaxBlockSize('none');
    }
  }, [inputRef, suggestions]);
  const [inlineSize, setInlineSize] = useState('auto');
  useEffect(() => {
    const input = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
    if (!input) {
      return;
    }
    const updateInlineSize = () => {
      setInlineSize(`${input.offsetWidth}px`);
    };
    updateInlineSize();
    const resizeObserver = new ResizeObserver(updateInlineSize);
    resizeObserver.observe(input);
    return () => {
      resizeObserver.disconnect();
    };
  }, [inputRef]);
  const [isKeyboardActive, setIsKeyboardActive] = useState(false);
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        setIsKeyboardActive(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const results = useMemo(() => {
    if (!suggestions) {
      return [];
    }
    return makeResults({
      disableResultsNavigation,
      suggestions,
      searchValue
    });
  }, [disableResultsNavigation, suggestions, searchValue]);
  return /*#__PURE__*/_jsx("div", {
    onMouseMove: () => setIsKeyboardActive(false),
    children: /*#__PURE__*/_jsx(CardWrapper, {
      borderRadius: "300",
      inlineSize: inlineSize,
      marginBlockEnd: "0",
      marginBlockStart: "100",
      maxBlockSize: maxBlockSize,
      overflowX: "hidden",
      overflowY: maxBlockSize === 'none' ? 'hidden' : 'scroll',
      position: "absolute",
      ref: containerRef,
      zIndex: "1111",
      children: /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(SearchAutocompleteResultsHeader, {
          searchValue: searchValue,
          disableResultsNavigation: disableResultsNavigation,
          onClick: () => onHeaderClick === null || onHeaderClick === void 0 ? void 0 : onHeaderClick(searchValue)
        }), results.length > 0 && results.map(result => /*#__PURE__*/_jsxs(Fragment, {
          children: [result.title, result.options.map((resultItemOption, resultItemOptionIndex) => /*#__PURE__*/_jsx(SearchAutocompleteResultItem, {
            itemComponent: result.itemComponent,
            option: resultItemOption,
            index: resultItemOptionIndex + result.startIndex,
            highlightedOptionIndex: highlightedOptionIndex,
            onMouseEnter: (option, index) => {
              // Prevent mouse events from interfering with keyboard navigation
              if (!isKeyboardActive) {
                onResultItemMouseEnter(option, index, result);
              }
            },
            onClick: (option, index) => {
              onResultItemClick(option, index, result);
            }
          }, `${result.type}-${resultItemOptionIndex}`))]
        }, result.type))]
      })
    })
  });
};